import { useCallback, useEffect, useRef } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useLocation } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import * as constants from '../Login/constants';
import { getTenantScopedRoute } from '../Login/customerId';
import { getSsoErrorFromSearch, hasPendingSsoCallbackParams } from '../Login/ssoError';

const DEFAULT_SSO_ERROR =
  `SSO login failed. Please contact ${constants.SUPPORT_EMAIL} for assistance.`;
const DEFAULT_REDIRECT_PATH = '/';

const getStoredSsoCustomerId = () => sessionStorage.getItem(constants.SSO_CUSTOMER_ID_STORAGE_KEY) || '';
const getLocationOrigin = () => {
  if (typeof window.location?.origin === 'string' && window.location.origin) {
    return window.location.origin;
  }

  return new URL(window.location?.href || 'http://localhost').origin;
};

const getStoredRedirect = () => {
  const redirectTo = sessionStorage.getItem('postLoginRedirect');
  if (!redirectTo) {
    return DEFAULT_REDIRECT_PATH;
  }

  try {
    const origin = getLocationOrigin();
    const redirectUrl = new URL(redirectTo, origin);

    if (redirectUrl.origin !== origin) {
      return DEFAULT_REDIRECT_PATH;
    }

    return `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}` || DEFAULT_REDIRECT_PATH;
  } catch (error) {
    return DEFAULT_REDIRECT_PATH;
  }
};

const getHubErrorMessage = (data) => {
  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  if (data?.message) {
    return data.message;
  }

  return DEFAULT_SSO_ERROR;
};

const replaceLocation = (nextLocation) => {
  // Use a full document navigation here so Amplify's Hosted UI state is fully reset and
  // the callback route does not remain in browser history after success or failure.
  if (typeof window.location?.replace === 'function') {
    window.location.replace(nextLocation);
    return;
  }

  window.location.href = nextLocation;
};

const AuthCallback = () => {
  const location = useLocation();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const hasHandledRedirect = useRef(false);
  const initialSearch = useRef(location.search);
  const initialSearchError = useRef(getSsoErrorFromSearch(initialSearch.current));
  const hasPendingCallbackParams = useRef(hasPendingSsoCallbackParams(initialSearch.current));

  const navigateToApp = useCallback(() => {
    if (hasHandledRedirect.current) {
      return;
    }

    hasHandledRedirect.current = true;
    const redirectTo = getStoredRedirect();
    sessionStorage.removeItem('postLoginRedirect');
    sessionStorage.removeItem(constants.SSO_CUSTOMER_ID_STORAGE_KEY);
    replaceLocation(redirectTo);
  }, []);

  const navigateToLogin = useCallback((message = DEFAULT_SSO_ERROR) => {
    if (hasHandledRedirect.current) {
      return;
    }

    hasHandledRedirect.current = true;
    sessionStorage.setItem('ssoError', message);
    const customerId = getStoredSsoCustomerId();
    const loginPath = getTenantScopedRoute(constants.ROUTE_LOGIN, customerId);
    sessionStorage.removeItem(constants.SSO_CUSTOMER_ID_STORAGE_KEY);
    replaceLocation(loginPath);
  }, []);

  useEffect(() => {
    const searchError = initialSearchError.current;
    if (searchError) {
      navigateToLogin(searchError);
      return undefined;
    }

    const stopListening = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          navigateToApp();
          break;
        case 'signInWithRedirect_failure':
          navigateToLogin(getHubErrorMessage(payload.data));
          break;
        default:
          break;
      }
    });

    return stopListening;
  }, [navigateToApp, navigateToLogin]);

  useEffect(() => {
    if (hasHandledRedirect.current || authStatus === 'configuring') {
      return;
    }

    if (authStatus === 'authenticated') {
      navigateToApp();
      return;
    }

    if (authStatus !== 'unauthenticated') {
      return;
    }

    if (!hasPendingCallbackParams.current) {
      navigateToLogin();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      navigateToLogin();
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authStatus, navigateToApp, navigateToLogin]);

  return <div>Signing you in...</div>;
};

export default AuthCallback;
